import {
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  LoaderCircle,
  RefreshCw,
  TriangleAlert,
  X,
} from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS, type CheckoutData } from "@/shared/contracts";
import { apiFetch, ApiClientError, swrFetcher } from "@/web/api";
import { Button } from "@/web/components/ui/button";
import { formatDate } from "@/web/lib/utils";

function countdown(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function isMobileBrowser() {
  const browserNavigator = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (browserNavigator.userAgentData?.mobile === true) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(browserNavigator.userAgent) ||
    (/Macintosh/i.test(browserNavigator.userAgent) && browserNavigator.maxTouchPoints > 1);
}

function merchantHome(returnUrl: string | null) {
  try {
    return returnUrl ? new URL(returnUrl).origin : "/";
  } catch {
    return "/";
  }
}

function loadImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("二维码图片加载失败"));
    image.src = source;
  });
}

const businessQrCrop = {
  left: 0.186,
  top: 0.288,
  size: 0.626,
};

function isBusinessQrPoster(image: HTMLImageElement) {
  const aspectRatio = image.naturalHeight / image.naturalWidth;
  return aspectRatio > 1.45 && aspectRatio < 1.55;
}

async function paymentCardBlob(qrSource: string, amount: string, businessQr: boolean) {
  const image = await loadImage(qrSource);
  const cropBusinessQr = businessQr && isBusinessQrPoster(image);
  const canvas = document.createElement("canvas");
  canvas.width = 900;
  canvas.height = 1120;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("无法生成支付卡片");

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.textAlign = "center";
  context.fillStyle = "#111827";
  context.font = '600 34px -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif';
  context.fillText("支付宝扫码支付", 450, 74);
  context.font = '600 78px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif';
  context.fillText(`¥${amount}`, 450, 172);

  const targetWidth = 700;
  const targetHeight = 700;
  if (cropBusinessQr) {
    const cropSize = image.naturalWidth * businessQrCrop.size;
    context.drawImage(
      image,
      image.naturalWidth * businessQrCrop.left,
      image.naturalHeight * businessQrCrop.top,
      cropSize,
      cropSize,
      100,
      220,
      targetWidth,
      targetHeight,
    );
  } else {
    const scale = Math.min(targetWidth / image.naturalWidth, targetHeight / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.drawImage(image, (canvas.width - drawWidth) / 2, 220 + (targetHeight - drawHeight) / 2, drawWidth, drawHeight);
  }

  context.fillStyle = "#4b5563";
  context.font = '400 27px -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif';
  context.fillText("扫码后请输入上方金额", 450, 980);
  context.fillStyle = "#9ca3af";
  context.font = '400 22px -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif';
  context.fillText("gptcard.shop", 450, 1040);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("无法生成支付卡片")), "image/png");
  });
}

async function savePaymentCard(blob: Blob, amount: string) {
  const filename = `gptcard-payment-${amount.replace(".", "-")}.png`;
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: "支付宝支付卡片" });
    return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function AlipayMark({ large = false }: { large?: boolean }) {
  return (
    <span className={`checkout-alipay-mark ${large ? "size-10" : "size-8"}`} aria-hidden="true">
      <img src="/alipay.svg" alt="" />
    </span>
  );
}

function CheckoutHeader() {
  return (
    <header className="checkout-header">
      <AlipayMark />
      <span>gptcard</span>
      <span className="checkout-header-divider">·</span>
      <span>支付宝</span>
    </header>
  );
}

function CheckoutShell({ children }: { children: ReactNode }) {
  return (
    <main className="checkout-page min-h-screen px-4 py-6 sm:py-10">
      <div className="checkout-shell mx-auto max-w-md">
        <CheckoutHeader />
        <section className="checkout-panel px-2 py-3 sm:px-8 sm:py-7">
          {children}
        </section>
      </div>
    </main>
  );
}

function LoadingCheckout() {
  return (
    <CheckoutShell>
      <div className="checkout-summary checkout-summary-loading" aria-hidden="true">
        <span className="checkout-skeleton checkout-skeleton-name" />
        <span className="checkout-skeleton checkout-skeleton-amount" />
      </div>
      <div className="checkout-stage">
        <div className="checkout-stage-layer checkout-stage-placeholder checkout-stage-visible" role="status" aria-live="polite">
          <AlipayMark large />
          <p>正在准备支付</p>
          <div className="checkout-progress" aria-hidden="true"><span /></div>
        </div>
      </div>
      <div className="checkout-support checkout-support-loading" aria-hidden="true">
        <span className="checkout-skeleton checkout-skeleton-line" />
        <span className="checkout-skeleton checkout-skeleton-status" />
      </div>
    </CheckoutShell>
  );
}

export function CheckoutPage() {
  const { token = "" } = useParams();
  const [now, setNow] = useState(Date.now());
  const [minimumLoadingElapsed, setMinimumLoadingElapsed] = useState(false);
  const [qrReady, setQrReady] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);
  const [cropBusinessQr, setCropBusinessQr] = useState(false);
  const [paymentCard, setPaymentCard] = useState<Blob | null>(null);
  const [savingCard, setSavingCard] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const mobileRedirectAttempted = useRef(false);
  const { data, error, isLoading, mutate } = useSWR<CheckoutData>(`/public-api/checkout/${encodeURIComponent(token)}`, swrFetcher, {
    refreshInterval: (latest) => {
      if (latest && (["paid", "late_paid"].includes(latest.status) || Date.parse(latest.monitor_until) <= Date.now())) return 0;
      return (latest?.payment_poll_interval_seconds ?? PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS) * 1_000;
    },
    shouldRetryOnError: false,
  });
  const { data: generatedQr } = useSWR(
    data?.collection_mode === "transfer" && data.payment_uri ? ["transfer-qr", data.payment_uri] : null,
    ([, uri]) => QRCode.toDataURL(uri, { width: 600, margin: 2, errorCorrectionLevel: "M", color: { dark: "#111827", light: "#ffffff" } }),
  );
  const qrSource = useMemo(
    () => data?.collection_mode === "business_qr" ? data.business_qr_url : generatedQr ?? "",
    [data?.business_qr_url, data?.collection_mode, generatedQr],
  );
  const paid = Boolean(data && (data.status === "paid" || data.status === "late_paid"));

  useEffect(() => {
    const timer = window.setTimeout(() => setMinimumLoadingElapsed(true), 360);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setQrReady(false);
    setQrFailed(false);
    setCropBusinessQr(false);
    setPaymentCard(null);
    if (!qrSource) return;

    let active = true;
    void loadImage(qrSource)
      .then((image) => {
        if (active) {
          setCropBusinessQr(data?.collection_mode === "business_qr" && isBusinessQrPoster(image));
          setQrReady(true);
        }
      })
      .catch(() => {
        if (active) setQrFailed(true);
      });
    return () => {
      active = false;
    };
  }, [data?.collection_mode, qrSource]);

  useEffect(() => {
    if (!qrReady || !qrSource || !data?.payable_money) return;
    let active = true;
    void paymentCardBlob(qrSource, data.payable_money, data.collection_mode === "business_qr")
      .then((blob) => {
        if (active) setPaymentCard(blob);
      })
      .catch(() => {
        if (active) setPaymentCard(null);
      });
    return () => {
      active = false;
    };
  }, [data?.collection_mode, data?.payable_money, qrReady, qrSource]);

  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") void mutate();
    };
    window.addEventListener("focus", refreshOnReturn);
    window.addEventListener("pageshow", refreshOnReturn);
    document.addEventListener("visibilitychange", refreshOnReturn);
    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      window.removeEventListener("pageshow", refreshOnReturn);
      document.removeEventListener("visibilitychange", refreshOnReturn);
    };
  }, [mutate]);

  useEffect(() => {
    if (!paid || !data?.return_target) return;
    const timer = window.setTimeout(() => window.location.assign(data.return_target!), 1_100);
    return () => window.clearTimeout(timer);
  }, [data?.return_target, paid]);

  useEffect(() => {
    if (
      mobileRedirectAttempted.current ||
      !data ||
      data.collection_mode !== "transfer" ||
      data.status !== "pending" ||
      !data.payment_uri ||
      Date.parse(data.expires_at) <= Date.now() ||
      !isMobileBrowser()
    ) return;

    mobileRedirectAttempted.current = true;
    window.location.assign(data.payment_uri);
  }, [data]);

  const copyAmount = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.payable_money);
      setActionMessage("金额已复制");
    } catch {
      setActionMessage("请记住页面显示的支付金额");
    }
  };

  const handleSavePaymentCard = async () => {
    if (!data || !paymentCard || savingCard) return;
    setSavingCard(true);
    setActionMessage("");
    try {
      await savePaymentCard(paymentCard, data.payable_money);
      const copied = await navigator.clipboard.writeText(data.payable_money).then(() => true).catch(() => false);
      setActionMessage(copied ? "金额已复制，请从支付宝相册选择" : "支付卡片已保存，请从支付宝相册选择");
    } catch (saveError) {
      if (saveError instanceof DOMException && saveError.name === "AbortError") {
        setActionMessage("已取消保存");
      } else {
        setActionMessage("保存失败，请长按二维码保存");
      }
    } finally {
      setSavingCard(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!data || canceling || paid) return;
    setCanceling(true);
    setActionMessage("");
    try {
      const result = await apiFetch<{ return_url: string | null }>(`/public-api/checkout/${encodeURIComponent(token)}/cancel`, { method: "POST" });
      window.location.assign(result.return_url || merchantHome(data.return_url));
    } catch (cancelError) {
      if (cancelError instanceof ApiClientError && cancelError.code === "ORDER_ALREADY_PAID") {
        await mutate();
        setCanceling(false);
        return;
      }
      setActionMessage("取消失败，请重试");
      setCanceling(false);
    }
  };

  if (isLoading || !minimumLoadingElapsed) return <LoadingCheckout />;

  if (error || !data) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <CheckoutShell>
        <div className="checkout-summary checkout-reveal-summary text-center">
          <p className="checkout-summary-name">支付宝收银台</p>
          <h1 className="checkout-state-heading">支付未完成</h1>
        </div>
        <div className="checkout-stage">
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible">
            <span className="checkout-state-icon checkout-state-icon-error"><TriangleAlert /></span>
            <h2>{notFound ? "支付订单不存在" : "暂时无法加载"}</h2>
            <p>{notFound ? "链接可能已经失效，请返回商城重新下单。" : "网络连接不稳定，请稍后重试。"}</p>
            {!notFound ? <Button variant="outline" size="sm" onClick={() => void mutate()}><RefreshCw />重新加载</Button> : null}
          </div>
        </div>
      </CheckoutShell>
    );
  }

  const checkoutExpired = now >= Date.parse(data.expires_at);
  const monitoringEnded = now >= Date.parse(data.monitor_until);
  const returnUrl = data.return_target || merchantHome(data.return_url);
  const queued = !paid && data.status === "pending" && !data.payment_ready && !checkoutExpired;
  const pending = !paid && data.payment_ready && !checkoutExpired && !monitoringEnded;
  const confirming = !paid && data.payment_ready && checkoutExpired && !monitoringEnded;
  const expired = !paid && (monitoringEnded || (!data.payment_ready && checkoutExpired));
  const qrAlt = data.collection_mode === "business_qr" ? "支付宝经营码" : "支付宝转账二维码";

  return (
    <CheckoutShell>
      <div className="checkout-summary checkout-reveal-summary text-center">
        <p className="checkout-summary-name">{data.name}</p>
        <div className="checkout-amount-row">
          <h1>¥{data.payable_money}</h1>
          {pending ? <button type="button" className="checkout-icon-button" title="复制金额" aria-label="复制金额" onClick={() => void copyAmount()}><Copy /></button> : null}
        </div>
      </div>

      <div className="checkout-stage" aria-live="polite">
        {qrSource ? (
          <div className={`checkout-stage-layer checkout-qr-shell ${pending && qrReady ? "checkout-stage-visible" : "checkout-stage-hidden"}`}>
            <div className="checkout-qr-viewport">
              <img src={qrSource} alt={qrAlt} className={cropBusinessQr ? "checkout-business-qr-image" : ""} />
            </div>
          </div>
        ) : null}

        {pending && !qrReady && !qrFailed ? (
          <div className="checkout-stage-layer checkout-stage-placeholder checkout-stage-visible" role="status">
            <LoaderCircle className="checkout-spinner" />
            <p>正在准备二维码</p>
          </div>
        ) : null}

        {pending && qrFailed ? (
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible">
            <span className="checkout-state-icon checkout-state-icon-error"><TriangleAlert /></span>
            <h2>二维码加载失败</h2>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}><RefreshCw />重新加载</Button>
          </div>
        ) : null}

        {queued ? (
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible checkout-queue-state" role="status">
            <span className="checkout-state-icon checkout-state-icon-waiting"><LoaderCircle className="checkout-spinner" /></span>
            <h2>正在准备支付</h2>
            <p>{data.queue_position > 0 ? `前方 ${data.queue_position} 笔订单` : "即将进入收银台"}</p>
            <span className="checkout-queue-note">轮到你后将自动显示二维码</span>
          </div>
        ) : null}

        {paid ? (
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible checkout-success-state">
            <span className="checkout-state-icon checkout-state-icon-success"><CheckCircle2 /></span>
            <h2>支付成功</h2>
            <p>{data.return_target ? "正在返回订单" : "付款结果已同步"}</p>
            {data.return_target ? <LoaderCircle className="checkout-return-spinner" /> : null}
            <Button variant="ghost" size="sm" asChild><a href={returnUrl}>立即返回<ExternalLink /></a></Button>
          </div>
        ) : null}

        {confirming ? (
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible">
            <span className="checkout-state-icon checkout-state-icon-waiting"><LoaderCircle className="checkout-spinner" /></span>
            <h2>正在确认付款</h2>
            <p>已付款请停留在此页面</p>
            <span className="checkout-state-countdown">确认窗口 {countdown(Date.parse(data.monitor_until) - now)}</span>
            <Button variant="ghost" size="sm" asChild><a href={merchantHome(data.return_url)}>返回商城</a></Button>
          </div>
        ) : null}

        {expired ? (
          <div className="checkout-stage-layer checkout-stage-state checkout-stage-visible">
            <span className="checkout-state-icon checkout-state-icon-error"><Clock3 /></span>
            <h2>支付订单已失效</h2>
            <p>请勿继续支付，返回商城重新下单。</p>
            <Button size="sm" asChild><a href={returnUrl}>返回商城<ExternalLink /></a></Button>
          </div>
        ) : null}
      </div>

      {pending ? (
        <div className="checkout-support checkout-reveal-support">
          {data.collection_mode === "business_qr" ? (
            <>
              <Button className="checkout-save-action w-full sm:hidden" size="lg" disabled={!paymentCard || savingCard} onClick={() => void handleSavePaymentCard()}>
                {savingCard ? <LoaderCircle className="animate-spin" /> : <Download />}{savingCard ? "正在生成" : "保存支付卡片"}
              </Button>
              <p className="checkout-instruction sm:hidden">保存后，在支付宝扫一扫中从相册选择</p>
              <p className="checkout-instruction hidden sm:block">打开支付宝扫一扫，扫码后输入上方金额</p>
            </>
          ) : data.payment_uri ? (
            <Button className="checkout-save-action w-full sm:hidden" size="lg" asChild><a href={data.payment_uri}>打开支付宝<ExternalLink /></a></Button>
          ) : null}

          <p className={`checkout-action-feedback ${actionMessage ? "checkout-action-feedback-visible" : ""}`} role="status">
            {actionMessage ? <><Check />{actionMessage}</> : null}
          </p>

          <div className="checkout-payment-status">
            <span className="checkout-status-dot" aria-hidden="true" />
            <span>等待付款</span>
            <span className="checkout-status-separator">·</span>
            <span className="checkout-countdown">{countdown(Date.parse(data.expires_at) - now)}</span>
          </div>

          <div className="checkout-order-disclosure">
            <button type="button" aria-expanded={orderDetailsOpen} aria-controls="checkout-order-details" onClick={() => setOrderDetailsOpen((open) => !open)}>
              <span>订单信息</span>
              <ChevronDown className={orderDetailsOpen ? "checkout-chevron-open" : ""} />
            </button>
            {orderDetailsOpen ? (
              <dl id="checkout-order-details" className="checkout-order-details">
                <div><dt>订单号</dt><dd>{data.out_trade_no}</dd></div>
                <div><dt>创建时间</dt><dd>{formatDate(data.created_at)}</dd></div>
              </dl>
            ) : null}
          </div>
          <button type="button" className="checkout-cancel-action" disabled={canceling} onClick={() => void handleCancelOrder()}>
            {canceling ? <LoaderCircle className="checkout-spinner" /> : <X />}
            {canceling ? "正在取消" : "取消订单"}
          </button>
        </div>
      ) : queued ? (
        <div className="checkout-support checkout-reveal-support">
          <p className={`checkout-action-feedback ${actionMessage ? "checkout-action-feedback-visible" : ""}`} role="status">
            {actionMessage}
          </p>
          <div className="checkout-payment-status checkout-queue-status">
            <span className="checkout-status-dot" aria-hidden="true" />
            <span>等待支付名额</span>
            <span className="checkout-status-separator">·</span>
            <span className="checkout-countdown">{countdown(Date.parse(data.expires_at) - now)}</span>
          </div>
          <div className="checkout-order-disclosure">
            <button type="button" aria-expanded={orderDetailsOpen} aria-controls="checkout-order-details" onClick={() => setOrderDetailsOpen((open) => !open)}>
              <span>订单信息</span>
              <ChevronDown className={orderDetailsOpen ? "checkout-chevron-open" : ""} />
            </button>
            {orderDetailsOpen ? (
              <dl id="checkout-order-details" className="checkout-order-details">
                <div><dt>订单号</dt><dd>{data.out_trade_no}</dd></div>
                <div><dt>创建时间</dt><dd>{formatDate(data.created_at)}</dd></div>
              </dl>
            ) : null}
          </div>
          <button type="button" className="checkout-cancel-action" disabled={canceling} onClick={() => void handleCancelOrder()}>
            {canceling ? <LoaderCircle className="checkout-spinner" /> : <X />}
            {canceling ? "正在取消" : "取消订单"}
          </button>
        </div>
      ) : null}
    </CheckoutShell>
  );
}
